import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../App';

const RefundPage = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0f0529]' : 'bg-[#1a0d3e]'} py-12 px-4`}>
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/')} className="text-purple-500 mb-8 hover:underline">
          ← Back to Home
        </button>

        <div className={`${isDark ? 'bg-[#241651] text-white' : 'bg-[#241651] text-white'} rounded-3xl p-8 md:p-12 shadow-lg`}>
          <h1 className="text-4xl font-bold mb-2">Refund Policy</h1>
          <p className={`${isDark ? 'text-purple-300' : 'text-purple-300'} mb-8`}>Last Updated: January 2026</p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4">1. Overview</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                At KwizHub, we want you to be completely satisfied with your subscription. This Refund Policy outlines the circumstances under which refunds may be issued for our premium subscription services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. Subscription Cancellation</h2>
              <div className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed space-y-3`}>
                <p>You may cancel your subscription at any time through your account settings or by contacting our support team.</p>
                <p><strong>When you cancel:</strong></p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Your subscription will remain active until the end of your current billing period</li>
                  <li>You will retain access to all premium features until the subscription expires</li>
                  <li>Your subscription will NOT automatically renew after cancellation</li>
                  <li>Your created quizzes will remain accessible even after downgrading to free tier</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. Refund Eligibility</h2>
              <div className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed space-y-3`}>
                <p><strong>Full Refunds:</strong> We offer full refunds within 7 days of your initial subscription purchase if:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>You have not extensively used premium features</li>
                  <li>You experienced technical issues that prevented you from using the Service</li>
                  <li>The Service did not function as advertised</li>
                </ul>
                
                <p className="mt-4"><strong>Partial Refunds:</strong> We may offer partial refunds or credits in cases of:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Extended service outages affecting your ability to host games</li>
                  <li>Significant feature changes that materially impact your use case</li>
                  <li>Billing errors or duplicate charges</li>
                </ul>
                
                <p className="mt-4"><strong>No Refunds:</strong> Refunds are generally not provided for:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Requests made after 7 days from purchase</li>
                  <li>Users who have extensively used premium features (created multiple quizzes, hosted games)</li>
                  <li>Change of mind after the 7-day period</li>
                  <li>Failure to cancel before renewal date</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. How to Request a Refund</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed mb-3`}>
                To request a refund, please contact our support team with:
              </p>
              <ul className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed list-disc pl-6 space-y-1`}>
                <li>Your account email address</li>
                <li>Date of purchase</li>
                <li>Reason for refund request</li>
                <li>Transaction ID or receipt (if available)</li>
              </ul>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed mt-3`}>
                Email: <a href="mailto:support@kwizhub.com" className="text-purple-500 hover:underline">support@kwizhub.com</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Refund Processing</h2>
              <div className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed space-y-3`}>
                <p><strong>Processing Time:</strong> Refund requests are typically reviewed within 3-5 business days.</p>
                <p><strong>Refund Method:</strong> Approved refunds will be credited back to the original payment method used for the purchase:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Paddle payments:</strong> Refunds processed through Paddle typically appear within 5-10 business days</li>
                  <li><strong>PayPal payments:</strong> Refunds processed through PayPal typically appear within 3-5 business days</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Annual Subscriptions</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                For annual subscriptions, the 7-day refund policy applies from the date of purchase. After 7 days, annual subscriptions are non-refundable but you may cancel to prevent future renewals. In exceptional circumstances, we may offer pro-rated refunds at our discretion.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Free Trial</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                If we offer a free trial period, no charges will be made during the trial. If you do not cancel before the trial ends, your subscription will begin and you will be charged according to your selected plan. Standard refund policies apply after the trial converts to a paid subscription.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Chargebacks</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                We encourage you to contact us directly before initiating a chargeback with your bank or payment provider. Filing a chargeback without first contacting us may result in immediate suspension of your account and loss of access to your created content.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">9. Changes to This Policy</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                KwizHub reserves the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting to the Service. Your continued use of the Service after any changes indicates your acceptance of the modified policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">10. Contact Us</h2>
              <p className={`${isDark ? 'text-purple-200' : 'text-purple-200'} leading-relaxed`}>
                If you have any questions about our Refund Policy, please contact us at:
              </p>
              <a href="mailto:support@kwizhub.com" className="text-purple-500 hover:underline">support@kwizhub.com</a>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPage;
